import React, { useState, useEffect } from 'react';
import { Image, Button, Spin, Space, Popconfirm, message } from 'antd';
import { ColumnType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import NoDraftBg from '../../imgs/no-draft.png';
import InvoiceModal from '../../../prosepectInfo/components/InvoiceModal';
import CancelInvoiceModal from 'components/ManageSubscription/components/PaymentTable/components/CancelInvoiceModal';
import useInvoiceDraftList from 'hooks/useInvoiceDraftList';
import CommonTable from 'components/CommonTable';
import InvoiceLabel from 'components/ManageSubscription/components/PaymentTable/components/InvoiceLabel';
import { TPaymentBilling } from 'types/provider';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import dayjs from 'utils/dayjs';
import s from './s.module.less';

interface Props {
    clientId?: number;
    state?: string;
    onDataChange?: () => void;
    isGroupPractice?: boolean;
}

const InvoiceDraftTable = ({ clientId, state, onDataChange, isGroupPractice }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState<TPaymentBilling | undefined>();
    const [paginationInfo, setPaginationInfo] = useState<any>({ current: 1, pageSize: 10 });
    const [data, total, loading, fetchData] = useInvoiceDraftList({
        clientId,
        current: 1,
    }, isGroupPractice);
    const [showCancelInvoiceModal, setShowCancelInvoiceModal] = useState(false);
    const [getDataUpdatedFlag] = DataUpdatedFlagStore.useStore();
    const billingDataUpdatedFlag = getDataUpdatedFlag('billingDataUpdated');
    React.useEffect(() => {
        if (billingDataUpdatedFlag) {
            fetchData({
                clientId,
                current: paginationInfo.current || 1,
                size: paginationInfo.pageSize,
            });
        }
    }, [billingDataUpdatedFlag, clientId]);

    const handleEdit = (item: TPaymentBilling) => {
        setEditingInvoice(item);
        setIsModalOpen(true);
    };

    const handleCancelInvoice = (item: TPaymentBilling) => {
        setEditingInvoice(item);
        setShowCancelInvoiceModal(true);
    };

    const handleTableChange = (pagination: any) => {
        setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
        });
    };

    React.useEffect(() => {
        fetchData({
            clientId,
            current: paginationInfo.current || 1,
            size: paginationInfo.pageSize,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationInfo, clientId]);

    const handleModalSuccess = () => {
        setIsModalOpen(false);
        setEditingInvoice(undefined);
        fetchData({
            clientId,
            current: paginationInfo.current || 1,
            size: paginationInfo.pageSize,
        });
        onDataChange?.();
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setEditingInvoice(undefined);
    };

    const columns: ColumnType<TPaymentBilling>[] = [
        {
            title: 'CHARGE TYPE',
            key: 'chargeType',
            width: 200,
            render: (_: any, record: TPaymentBilling) => {
                return <InvoiceLabel record={record} />;
            },
        },
        ...(isGroupPractice
            ? [
                  {
                      title: 'PROVIDER',
                      key: 'providerName',
                      width: 150,
                      render: (_: any, record: TPaymentBilling) => {
                          const name = record.provider ? `${record.provider.firstName || ''} ${record.provider.lastName || ''}`.trim() : '';
                          return name || '-';
                      },
                  } as ColumnType<TPaymentBilling>,
              ]
            : []),
        {
            title: 'LAST MODIFIED',
            dataIndex: 'updatedAt',
            key: 'lastModified',
            width: 150,
            sorter: (a: TPaymentBilling, b: TPaymentBilling) =>
                new Date(a.updatedAt || '').getTime() - new Date(b.updatedAt || '').getTime(),
            render: (date: string) => {
                return date ? dayjs(date).format('MM/DD/YYYY') : '-';
            },
        },
        {
            title: 'ISSUE DATE',
            dataIndex: 'issueDate',
            key: 'issueDate',
            width: 150,
            sorter: (a: TPaymentBilling, b: TPaymentBilling) =>
                new Date(a.issueDate || '').getTime() - new Date(b.issueDate || '').getTime(),
            render: (date: string) => {
                return date ? dayjs(date).format('MM/DD/YYYY') : '-';
            },
        },
        {
            title: 'DUE DATE',
            dataIndex: 'dueDate',
            key: 'dueDate',
            width: 150,
            sorter: (a: TPaymentBilling, b: TPaymentBilling) =>
                new Date(a.dueDate || '').getTime() - new Date(b.dueDate || '').getTime(),
            render: (date: string) => {
                return date ? dayjs(date).format('MM/DD/YYYY') : '-';
            },
        },
        {
            title: '',
            key: 'action',
            width: 120,
            align: 'right',
            render: (_: any, record: TPaymentBilling) => (
                <Space size="middle">
                    <Button type="link" onClick={() => handleEdit(record)} className={s.editButton}>
                        Edit
                    </Button>
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        className={s.deleteButton}
                        onClick={() => handleCancelInvoice(record)}
                    />
                </Space>
            ),
        },
    ];

    const renderEmpty = () => {
        return (
            <div className={s.emptyState}>
                <div className={s.emptyIllustration}>
                    <Image src={NoDraftBg} alt="No draft" width={264} height={209} preview={false} />
                </div>
                <div className={s.emptyTitle}>No invoice draft yet</div>
                <div className={s.emptyDescription}>Create and send an invoice to your patient today</div>
                <Button type="primary" onClick={() => setIsModalOpen(true)} className={s.emptyButton}>
                    Create new invoice
                </Button>
            </div>
        );
    };

    if (loading) {
        return <Spin />;
    }

    return (
        <div>
            {data?.length === 0 || !data ? (
                renderEmpty()
            ) : (
                <CommonTable
                    data={data || []}
                    columns={columns}
                    pagination={{
                        current: paginationInfo.current || 1,
                        pageSize: paginationInfo.pageSize,
                        total,
                        showTotal: (total) => `${total} records`,
                    }}
                    onChange={handleTableChange}
                    loading={loading}
                    rowKey="id"
                />
            )}
            <InvoiceModal
                open={isModalOpen}
                onCancel={handleModalCancel}
                onSuccess={handleModalSuccess}
                invoiceNo={editingInvoice?.invoiceNo}
                id={editingInvoice?.id}
                clientId={clientId}
                state={state}
            />
            <CancelInvoiceModal
                open={showCancelInvoiceModal}
                onCancel={() => {
                    setShowCancelInvoiceModal(false);
                }}
                item={editingInvoice!}
                onSuccess={() => {
                    setShowCancelInvoiceModal(false);
                    fetchData({
                        clientId,
                        current: paginationInfo.current || 1,
                        size: paginationInfo.pageSize,
                    });
                    onDataChange?.();
                }}
            />
        </div>
    );
};

export default InvoiceDraftTable;
